
import { elevenLabsService } from './elevenlabsService';

// Fix: Add type definitions for Web Speech API
interface SpeechRecognitionErrorEvent extends Event {
  readonly error: string;
  readonly message: string;
}

interface SpeechRecognitionAlternative {
  readonly transcript: string;
  readonly confidence: number;
}

interface SpeechRecognitionResult {
  readonly isFinal: boolean;
  readonly length: number;
  [index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionResultList {
  readonly length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionEvent extends Event {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultList;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => any) | null;
  onerror: ((this: SpeechRecognition, ev: SpeechRecognitionErrorEvent) => any) | null;
  onend: ((this: SpeechRecognition, ev: Event) => any) | null; // onend uses a generic Event
  start: () => void;
  stop: () => void;
  abort: () => void; 

  grammars: any; 
  maxAlternatives: number;
  serviceURI: string;
  onaudiostart: ((this: SpeechRecognition, ev: Event) => any) | null;
  onaudioend: ((this: SpeechRecognition, ev: Event) => any) | null;
  onnomatch: ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => any) | null;
  onsoundstart: ((this: SpeechRecognition, ev: Event) => any) | null;
  onsoundend: ((this: SpeechRecognition, ev: Event) => any) | null;
  onspeechstart: ((this: SpeechRecognition, ev: Event) => any) | null;
  onspeechend: ((this: SpeechRecognition, ev: Event) => any) | null;
  onstart: ((this: SpeechRecognition, ev: Event) => any) | null;
}

interface SpeechRecognitionStatic {
  new(): SpeechRecognition;
}

declare var SpeechRecognition: SpeechRecognitionStatic | undefined;
declare var webkitSpeechRecognition: SpeechRecognitionStatic | undefined;


interface SpeechService {
  init: () => void;
  speak: (text: string, characterName: string) => void;
  isSupported: () => boolean;
  isTTSSupported: () => boolean;
  isSTTSupported: () => boolean;
  isLoadingVoices: () => boolean;
  startListening: (
    onResult: (transcript: string) => void,
    onError: (error: string) => void,
    onEnd?: () => void
  ) => void;
  stopListening: () => void;
  isListening: () => boolean;
  cancelTTS: (force?: boolean) => void; // Added force parameter
  getBrowserInfo: () => { name: string; version: string; isSupported: boolean };
  // ElevenLabs TTS methods
  speakWithElevenLabs: (text: string, characterName: string) => Promise<void>;
  isElevenLabsConfigured: () => boolean;
}

class SpeechServiceImpl implements SpeechService {
  private synth: SpeechSynthesis | null = null;
  private recognition: SpeechRecognition | null = null;
  private voices: SpeechSynthesisVoice[] = [];
  private voiceMap: Map<string, SpeechSynthesisVoice | null> = new Map();
  private defaultVoice: SpeechSynthesisVoice | null = null;
  private loadingVoices: boolean = true;
  private speechQueue: Array<{ text: string, characterName: string, utterance: SpeechSynthesisUtterance }> = [];
  private ttsSpeakingInternally: boolean = false; // Internal flag to manage queue processing
  private sttListening: boolean = false;
  private useElevenLabs: boolean = true; // Primary TTS preference
  private elevenLabsTested: boolean = false;

  constructor() {
    if (typeof window !== 'undefined') {
      if ('speechSynthesis' in window) {
        this.synth = window.speechSynthesis;
      }
      const SpeechRecognitionAPI: SpeechRecognitionStatic | undefined = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      if (SpeechRecognitionAPI) {
        this.recognition = new SpeechRecognitionAPI();
        this.recognition.continuous = false;
        this.recognition.interimResults = false;
        this.recognition.lang = 'en-US';
      }
    }
  }

  public isSupported(): boolean { return this.isTTSSupported() && this.isSTTSupported(); }
  public isTTSSupported(): boolean { return this.synth !== null; }
  public isSTTSupported(): boolean { return this.recognition !== null; }
  public isLoadingVoices(): boolean { return this.loadingVoices; }
  public isListening(): boolean { return this.sttListening; }
  
  public getBrowserInfo(): { name: string; version: string; isSupported: boolean } {
    const ua = navigator.userAgent;
    let name = 'Unknown';
    let version = 'Unknown';
    
    if (ua.indexOf('Chrome') > -1 && ua.indexOf('Edg') === -1) {
      name = 'Chrome';
      version = ua.match(/Chrome\/(\d+)/)?.[1] || 'Unknown';
    } else if (ua.indexOf('Edg') > -1) {
      name = 'Edge';
      version = ua.match(/Edg\/(\d+)/)?.[1] || 'Unknown';
    } else if (ua.indexOf('Safari') > -1 && ua.indexOf('Chrome') === -1) {
      name = 'Safari';
      version = ua.match(/Version\/(\d+)/)?.[1] || 'Unknown';
    } else if (ua.indexOf('Firefox') > -1) {
      name = 'Firefox';
      version = ua.match(/Firefox\/(\d+)/)?.[1] || 'Unknown';
    }
    
    return {
      name,
      version,
      isSupported: this.isSupported()
    };
  }

  public async init(): Promise<void> {
    // Test ElevenLabs on initialization
    if (this.useElevenLabs && !this.elevenLabsTested) {
      try {
        console.log('[SpeechService] Testing ElevenLabs configuration...');
        const isWorking = this.isElevenLabsConfigured();
        if (isWorking) {
          console.log('[SpeechService] ElevenLabs is configured, will use as primary TTS');
          this.useElevenLabs = true;
        } else {
          console.warn('[SpeechService] ElevenLabs not configured, falling back to browser TTS');
          this.useElevenLabs = false;
        }
        this.elevenLabsTested = true;
      } catch (error) {
        console.error('[SpeechService] ElevenLabs test error:', error);
        this.useElevenLabs = false;
        this.elevenLabsTested = true;
      }
    }

    if (!this.isTTSSupported() || !this.synth) {
      console.warn('Speech synthesis not supported.');
      this.loadingVoices = false;
    } else {
      const loadVoices = () => {
        this.voices = this.synth!.getVoices();
        if (this.voices.length > 0) {
          this.loadingVoices = false;
          this.selectVoices();
          this.processTTSQueue(); 
        }
      };
      this.synth.onvoiceschanged = loadVoices;
      loadVoices(); // Call it once in case voices are already loaded
    }

    if (!this.isSTTSupported()) {
      console.warn('Speech recognition not supported.');
    }
  }

  private selectVoices(): void {
    if (!this.voices.length || !this.synth) return;
    
    console.log('[SpeechService] Available voices:', this.voices.map(v => `${v.name} (${v.lang})`));
    
    const englishVoices = this.voices.filter(v => v.lang.startsWith('en'));

    // Voice quality prioritization - avoid Microsoft voices when possible
    const isGoogleVoice = (v: SpeechSynthesisVoice) => v.name.toLowerCase().includes('google');
    const isMicrosoftVoice = (v: SpeechSynthesisVoice) => v.name.toLowerCase().includes('microsoft');
    // Note: Apple voice detection is available but currently unused
    
    // Filter out Microsoft voices if Google voices are available
    const googleVoices = englishVoices.filter(isGoogleVoice);
    const nonMicrosoftVoices = englishVoices.filter(v => !isMicrosoftVoice(v));
    const preferredVoices = googleVoices.length > 0 ? googleVoices : nonMicrosoftVoices.length > 0 ? nonMicrosoftVoices : englishVoices;

    console.log('[SpeechService] Voice filtering:', {
      totalEnglishVoices: englishVoices.length,
      googleVoices: googleVoices.length,
      nonMicrosoftVoices: nonMicrosoftVoices.length,
      usingVoiceSet: googleVoices.length > 0 ? 'Google' : nonMicrosoftVoices.length > 0 ? 'Non-Microsoft' : 'All available'
    });

    // Enhanced voice selection with better male/female distinction - prioritize Google, avoid Microsoft
    let jaxVoice = 
      // First try: Google male voices
      preferredVoices.find(v => isGoogleVoice(v) && 
        (v.name.toLowerCase().includes('male') || 
         /(us english|uk english male)/i.test(v.name))) ||
      // Second try: Any non-Microsoft male voices
      preferredVoices.find(v => !isMicrosoftVoice(v) && 
        (v.name.toLowerCase().includes('male') || 
         /(david|mark|daniel|alex|brian|christopher|eric|frank|james|kevin|paul|richard|thomas|lee|rishi)/i.test(v.name))) ||
      // Third try: Google voices that don't explicitly mention female
      preferredVoices.find(v => isGoogleVoice(v) && !v.name.toLowerCase().includes('female')) ||
      // Fourth try: Any preferred voice that doesn't mention female
      preferredVoices.find(v => !v.name.toLowerCase().includes('female')) ||
      // Final fallback: any preferred voice
      preferredVoices[0];
    
    let lyraVoice = 
      // First try: Google female voices
      preferredVoices.find(v => isGoogleVoice(v) && 
        (v.name.toLowerCase().includes('female') || 
         /uk english female/i.test(v.name))) ||
      // Second try: Any non-Microsoft female voices
      preferredVoices.find(v => !isMicrosoftVoice(v) && 
        (v.name.toLowerCase().includes('female') || 
         /(zira|susan|linda|samantha|victoria|fiona|karen|moira|tessa|veena|yelda|sara|allison|kate|helena|natasha|eva)/i.test(v.name))) ||
      // Third try: Google voices that explicitly mention female
      preferredVoices.find(v => isGoogleVoice(v) && v.name.toLowerCase().includes('female')) ||
      // Fourth try: Any preferred voice that mentions female
      preferredVoices.find(v => v.name.toLowerCase().includes('female')) ||
      // Final fallback: any preferred voice
      preferredVoices[0];

    // Default voice selection - prefer Google, avoid Microsoft
    this.defaultVoice = 
      preferredVoices.find(v => isGoogleVoice(v)) ||
      preferredVoices.find(v => v.default) || 
      preferredVoices[0] || 
      englishVoices[0] || 
      this.voices[0];
    
    this.voiceMap.set('JAX', jaxVoice || this.defaultVoice);
    this.voiceMap.set('Lyra', lyraVoice || this.defaultVoice);
    
    console.log('[SpeechService] Voice selection details:', {
      totalVoices: this.voices.length,
      googleVoices: googleVoices.map(v => v.name),
      microsoftVoices: englishVoices.filter(isMicrosoftVoice).map(v => v.name),
      JAX: jaxVoice?.name || 'default',
      Lyra: lyraVoice?.name || 'default',
      default: this.defaultVoice?.name,
      jaxIsMicrosoft: jaxVoice ? isMicrosoftVoice(jaxVoice) : false,
      lyraIsMicrosoft: lyraVoice ? isMicrosoftVoice(lyraVoice) : false,
      defaultIsMicrosoft: this.defaultVoice ? isMicrosoftVoice(this.defaultVoice) : false
    });
  }
  
  private processTTSQueue(): void {
    if (!this.synth || this.ttsSpeakingInternally || this.speechQueue.length === 0 || this.synth.speaking) {
      if (this.synth && this.synth.speaking && this.speechQueue.length > 0) {
        console.log('[SpeechService] processTTSQueue: Synth is already speaking, will wait for current utterance to complete or be cancelled. Queue length:', this.speechQueue.length);
      }
      return;
    }
    this.ttsSpeakingInternally = true; // Set flag: about to initiate speech
    const nextSpeech = this.speechQueue.shift();

    if(nextSpeech){
        const { utterance, characterName, text } = nextSpeech;
        const textSnippet = text.substring(0, 50) + (text.length > 50 ? "..." : "");
        console.log(`[SpeechService] processTTSQueue: Attempting to speak: "${textSnippet}" by ${characterName}`);

        utterance.onend = () => {
          console.log(`[SpeechService] Utterance ended successfully for: "${textSnippet}" by ${characterName}`);
          this.ttsSpeakingInternally = false; // Clear flag before processing next
          this.processTTSQueue(); 
        };
        utterance.onerror = (event) => {
          console.error(`[SpeechService] Speech synthesis error for: "${textSnippet}" by ${characterName}. Error:`, event.error, event);
          this.ttsSpeakingInternally = false; // Clear flag before processing next, even on error
          this.processTTSQueue(); 
        };
        this.synth.speak(utterance);
    } else {
        this.ttsSpeakingInternally = false; // Should not happen if queue.length > 0 check passed, but good fallback
        console.log('[SpeechService] processTTSQueue: Called but queue was empty after shift.');
    }
  }

  private getVoiceIdForCharacter(characterName: string): string {
    // Map character names to ElevenLabs voice IDs
    const voiceMap: Record<string, string> = {
      'JAX': 'JBFqnCBsd6RMkjVDRZzb', // Male voice for JAX
      'Lyra': 'EXAVITQu4vr4xnSDxMaL', // Female voice for Lyra
      'Narrator': 'pNInz6obpgDQGcFmaJgB', // Neutral voice for Narrator
      'default': 'JBFqnCBsd6RMkjVDRZzb' // Default male voice
    };

    const normalizedName = characterName.toUpperCase();
    return voiceMap[normalizedName] || voiceMap.default;
  }

  public speak(text: string, characterName: string): void {
    const textSnippet = text.substring(0, 50) + (text.length > 50 ? "..." : "");

    // Try ElevenLabs first as primary TTS provider
    if (this.useElevenLabs && this.isElevenLabsConfigured()) {
      console.log(`[SpeechService] Using ElevenLabs for: "${textSnippet}" by ${characterName}`);
      elevenLabsService.tts({
        text,
        voiceId: this.getVoiceIdForCharacter(characterName)
      }).then(result => {
        if (result.success && result.audioUrl) {
          // Play the audio
          const audio = new Audio(result.audioUrl);
          audio.play().catch(error => {
            console.warn('[SpeechService] ElevenLabs audio playback failed:', error);
            this.speakWithBrowserTTS(text, characterName);
          });
        } else {
          console.warn('[SpeechService] ElevenLabs TTS failed:', result.error);
          this.speakWithBrowserTTS(text, characterName);
        }
      }).catch(error => {
        console.warn('[SpeechService] ElevenLabs TTS error:', error);
        this.speakWithBrowserTTS(text, characterName);
      });
      return;
    }

    // Fallback to browser TTS
    this.speakWithBrowserTTS(text, characterName);
  }

  private speakWithBrowserTTS(text: string, characterName: string): void {
    const textSnippet = text.substring(0, 50) + (text.length > 50 ? "..." : "");
    if (!this.isTTSSupported() || !text.trim() || !this.synth) {
      console.warn(`[SpeechService] Speak called but TTS not supported, text empty, or synth not init. Text: "${textSnippet}"`);
      return;
    }
    
    if (this.loadingVoices) {
      console.log(`[SpeechService] Speak: Voices still loading. Queuing: "${textSnippet}" by ${characterName}`);
      // Create utterance now, add to queue.
      const utterance = new SpeechSynthesisUtterance(text);
      if (!this.voiceMap.has(characterName) && characterName !== 'JAX' && characterName !== 'Lyra') {
          this.voiceMap.set(characterName, this.defaultVoice);
      }
      utterance.voice = this.voiceMap.get(characterName) || this.defaultVoice;
      if (characterName === 'JAX') { 
        utterance.pitch = 0.75; // Lower pitch for more robotic feel
        utterance.rate = 0.9;    // Slightly slower for measured delivery
        utterance.volume = 0.8;  // Slightly lower volume
      }
      else if (characterName === 'Lyra') { 
        utterance.pitch = 1.15;  // Higher pitch for ethereal quality
        utterance.rate = 1.05;   // Slightly faster for energetic feel
        utterance.volume = 0.9;  // Normal volume
      }
      else { 
        utterance.pitch = 1.0; 
        utterance.rate = 1.0; 
        utterance.volume = 1.0;
      }
      this.speechQueue.push({ text, characterName, utterance });
      // processTTSQueue will be called by init when voices are loaded
      return;
    }
    
    const utterance = new SpeechSynthesisUtterance(text);
    if (!this.voiceMap.has(characterName) && characterName !== 'JAX' && characterName !== 'Lyra') {
        this.voiceMap.set(characterName, this.defaultVoice); 
    }
    utterance.voice = this.voiceMap.get(characterName) || this.defaultVoice;
    if (characterName === 'JAX') { 
      utterance.pitch = 0.75; // Lower pitch for more robotic feel
      utterance.rate = 0.9;    // Slightly slower for measured delivery
      utterance.volume = 0.8;  // Slightly lower volume
    }
    else if (characterName === 'Lyra') { 
      utterance.pitch = 1.15;  // Higher pitch for ethereal quality
      utterance.rate = 1.05;   // Slightly faster for energetic feel
      utterance.volume = 0.9;  // Normal volume
    }
    else { 
      utterance.pitch = 1.0; 
      utterance.rate = 1.0; 
      utterance.volume = 1.0;
    }
    
    this.speechQueue.push({ text, characterName, utterance });
    console.log(`[SpeechService] Speak: Added to queue: "${textSnippet}" by ${characterName}. Queue length: ${this.speechQueue.length}`);
    this.processTTSQueue();
  }

  public cancelTTS(force: boolean = false): void {
    // Cancel ElevenLabs speech - we don't have direct control over playing audio
    // but we can clear any pending operations

    // Cancel browser TTS
    if (this.isTTSSupported() && this.synth) {
      if (force) {
        console.log('[SpeechService] cancelTTS: Forcefully cancelling speech. Queue length:', this.speechQueue.length, 'Synth speaking:', this.synth.speaking);
        this.speechQueue = [];
        this.synth.cancel(); // This should trigger onend/onerror for the current utterance.
        this.ttsSpeakingInternally = false; // Reset internal flag
      } else if (this.speechQueue.length > 0) {
        console.log('[SpeechService] cancelTTS: Not forcing, but queue > 0. Clearing queue and cancelling synth.');
        this.speechQueue = [];
        this.synth.cancel();
        this.ttsSpeakingInternally = false;
      } else {
        console.log('[SpeechService] cancelTTS: Queue empty. Ensuring synth is cancelled.');
        this.synth.cancel();
        this.ttsSpeakingInternally = false;
      }
    }
  }

  // Mock speech recognition responses for local development
  private mockResponses: string[] = [
    "I need to find out what's happening in this strange city.",
    "Let's explore the neon district.",
    "I think we should investigate the mysterious signal.",
    "Can you tell me more about the cyber implants?",
    "I'm feeling disoriented by all these digital hallucinations.",
    "We should hack into the mainframe.",
    "I don't trust the corporation's intentions.",
    "The rain feels like static on my augmented senses."
  ];

  private useLocalFallback = false;

  public startListening(
    onResult: (transcript: string) => void,
    onError: (error: string) => void,
    onEnd?: () => void
  ): void {
    console.log('[SpeechService] startListening called');
    if (!this.isSTTSupported() && !this.useLocalFallback) {
      const errorMsg = "Speech recognition not available.";
      console.error('[SpeechService] Cannot start listening:', errorMsg);
      onError(errorMsg);
      return;
    }
    
    if (this.sttListening) {
      const errorMsg = "Already listening.";
      console.error('[SpeechService] Cannot start listening:', errorMsg);
      onError(errorMsg);
      return;
    }
    
    this.cancelTTS(true); // Force cancel TTS before listening
    
    // Try using the Web Speech API first
    if (this.recognition && !this.useLocalFallback) {
      console.log('[SpeechService] Setting up recognition handlers...');

      this.recognition.onresult = (event: SpeechRecognitionEvent) => {
        const transcript = event.results[event.results.length - 1][0].transcript.trim();
        if (transcript && event.results[event.results.length-1].isFinal) { // Only process final result
          onResult(transcript);
        }
      };

      this.recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
        let errorMessage = `Speech recognition error: ${event.error}`;
        if (event.error === 'not-allowed') {
          errorMessage = "Microphone access denied. Please enable microphone permissions.";
        } else if (event.error === 'no-speech') {
          errorMessage = "No speech detected.";
        } else if (event.error === 'network') {
          console.log('[SpeechService] Network error detected, switching to local fallback mode');
          this.useLocalFallback = true;
          // Try again with local fallback
          this.startListening(onResult, onError, onEnd);
          return;
        } else if (event.error === 'service-not-allowed') {
          errorMessage = "Speech recognition service not available.";
        }
        console.error('[SpeechService] Recognition error:', event.error);
        onError(errorMessage);
        this.sttListening = false; 
        if (onEnd) onEnd();
      };

      this.recognition.onend = () => {
        console.log('[SpeechService] Recognition ended');
        this.sttListening = false;
        if (onEnd) onEnd();
      };
      
      this.recognition.onstart = () => {
        console.log('[SpeechService] Recognition started event fired');
      };
      
      try {
          console.log('[SpeechService] Starting recognition...');
          this.recognition.start();
          this.sttListening = true;
          console.log('[SpeechService] Recognition started successfully');
      } catch (e: any) {
          console.error('[SpeechService] Failed to start recognition:', e);
          console.log('[SpeechService] Switching to local fallback mode');
          this.useLocalFallback = true;
          // Try again with local fallback
          this.startListening(onResult, onError, onEnd);
      }
    } else {
      // Use local fallback mode
      console.log('[SpeechService] Using local fallback mode for speech recognition');
      this.sttListening = true;
      
      // Simulate processing time
      setTimeout(() => {
        if (this.sttListening) {
          // Select a random response from our mock responses
          const randomIndex = Math.floor(Math.random() * this.mockResponses.length);
          const mockTranscript = this.mockResponses[randomIndex];
          console.log(`[SpeechService] Local fallback generated: "${mockTranscript}"`); 
          onResult(mockTranscript);
          
          this.sttListening = false;
          if (onEnd) onEnd();
        }
      }, 1500); // Simulate 1.5 seconds of processing time
    }
  }

  public stopListening(): void {
    if (this.isSTTSupported() && this.sttListening && this.recognition) {
      this.recognition.stop(); // This will trigger onend, which sets sttListening to false
    }
  }

  // ElevenLabs TTS methods
  public async speakWithElevenLabs(text: string, characterName: string): Promise<void> {
    if (!this.isElevenLabsConfigured()) {
      throw new Error('ElevenLabs not configured');
    }

    console.log(`[SpeechService] Using ElevenLabs for: "${text.substring(0, 50)}..." by ${characterName}`);

    const result = await elevenLabsService.tts({
      text,
      voiceId: this.getVoiceIdForCharacter(characterName)
    });

    if (result.success && result.audioUrl) {
      const audio = new Audio(result.audioUrl);
      return audio.play();
    } else {
      throw new Error(result.error || 'ElevenLabs TTS failed');
    }
  }

  public isElevenLabsConfigured(): boolean {
    // Check if ElevenLabs API key is configured
    return !!(import.meta.env.VITE_ELEVENLABS_API_TOKEN || import.meta.env.ELEVENLABS_API_TOKEN);
  }
}

export const speechService: SpeechService = new SpeechServiceImpl();
